import { pageTitle } from '@palaccount/config';
import type { ReceiptResponse } from '@palaccount/contracts';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Plus, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import { Amount, Ltr } from '@/components/amount';
import { Gated } from '@/components/permission-gate';
import { Pager } from '@/components/pager';
import { DocStatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AccountingTabs } from '@/features/accounts/accounting-tabs';
import { usePagedState, usePageRange } from '@/lib/paging';
import { useDebounced } from '@/lib/typeahead';

import { useReceipts } from './api';

const columnHelper = createColumnHelper<ReceiptResponse>();

/**
 * FIN-03's list: voucher number, date, customer (or other income), amount,
 * amount applied, and the lifecycle chip — Posted drawn GREEN here (§7: the
 * money is in the books).
 */
export function ReceiptsListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'' | ReceiptResponse['status']>('');
  /** RV-01 — number, customer or reference; a keystroke is not a request, so it is debounced. */
  const [q, setQ] = useState('');
  const search = useDebounced(q.trim());
  /** NFR-42 — the page rides in the URL, and the status filter resets it. */
  const paging = usePagedState();

  useEffect(() => {
    document.title = pageTitle(t('receipts.title'));
  }, [t]);

  const { data, isPending, isError } = useReceipts(
    { status: status || undefined, q: search || undefined },
    paging.page,
    paging.pageSize,
  );
  const receipts = data?.receipts;
  const range = usePageRange(data, paging.pageSize);

  const columns = useMemo(
    () => [
      columnHelper.accessor('code', {
        header: () => t('receipts.colNo'),
        cell: (info) => <Ltr value={info.getValue()} className="font-mono text-muted-foreground" />,
      }),
      columnHelper.accessor('receiptDate', {
        header: () => t('common.date'),
        cell: (info) => <Ltr value={info.getValue()} className="font-mono" />,
      }),
      columnHelper.accessor('partnerName', {
        header: () => t('receipts.colCustomer'),
        cell: (info) => (
          <span className="font-medium">{info.getValue() ?? t('receipts.otherIncome')}</span>
        ),
      }),
      columnHelper.accessor('amount', {
        header: () => <div className="text-end">{t('receipts.colAmount')}</div>,
        cell: (info) => (
          <div className="text-end">
            <Amount value={info.getValue()} currency={info.row.original.currency} />
          </div>
        ),
      }),
      // S6 — the head's method is the rows' summary: one method, or Mixed.
      columnHelper.accessor('method', {
        header: () => t('tenders.colMethod'),
        cell: (info) => (
          <span className="text-muted-foreground">{t(`receipts.method.${info.getValue()}`)}</span>
        ),
      }),
      columnHelper.accessor('totalApplied', {
        header: () => <div className="text-end">{t('receipts.colApplied')}</div>,
        cell: (info) => (
          <div className="text-end">
            <Amount value={info.getValue()} currency={info.row.original.currency} />
          </div>
        ),
      }),
      columnHelper.accessor('status', {
        header: () => t('receipts.colStatus'),
        cell: (info) => (
          <span className="inline-flex items-center gap-1.5">
            <DocStatusChip status={info.getValue()} postedTone="green" />
            {/* R8: a reversed voucher STAYS posted — the stamp is a second
                fact beside the status, so the register can tell them apart. */}
            {info.row.original.reversedAt ? (
              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-950 dark:text-blue-300">
                {t('common.reversed')}
              </span>
            ) : info.row.original.partiallyReversed ? (
              // FIN-10 §6 per tender: some of its cheques bounced; the amount
              // shown is what remains.
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                {t('receipts.partiallyReversed')}
              </span>
            ) : null}
          </span>
        ),
      }),
    ],
    [t],
  );

  const table = useReactTable({
    data: receipts ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="grid gap-4">
      <AccountingTabs />
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('receipts.title')}</h2>
        <Gated permission="accounting.create" reasonKey="perm.cannot.createReceiptVouchers">
          <Button asChild>
            <Link to="/accounting/receipts/new">
              <Plus aria-hidden /> {t('common.new')}
            </Link>
          </Button>
        </Gated>
      </div>

      <Card>
        <CardContent className="grid gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-full max-w-[320px]">
              <Search
                className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                aria-label={t('receipts.searchLabel')}
                placeholder={t('receipts.searchPh')}
                value={q}
                onChange={(event) => paging.change(setQ)(event.target.value)}
                className="h-10 ps-9 text-sm"
              />
            </div>
            <NativeSelect
              aria-label={t('receipts.filterStatus')}
              value={status}
              onChange={(event) => paging.change(setStatus)(event.target.value as typeof status)}
              className="h-10 w-auto text-sm"
            >
              <option value="">{t('sales.filterStatusAll')}</option>
              <option value="draft">{t('common.status.draft')}</option>
              <option value="posted">{t('common.status.posted')}</option>
              <option value="cancelled">{t('common.status.cancelled')}</option>
            </NativeSelect>
          </div>

          {isPending ? (
            <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy />
          ) : isError ? (
            <p role="alert" className="py-8 text-center text-sm text-destructive">
              {t('common.loadFailed')}
            </p>
          ) : table.getRowModel().rows.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <p className="text-sm text-muted-foreground">{t('receipts.empty')}</p>
              <Gated permission="accounting.create" reasonKey="perm.cannot.createReceiptVouchers">
                <Button asChild variant="outline">
                  <Link to="/accounting/receipts/new">
                    <Plus aria-hidden /> {t('common.new')}
                  </Link>
                </Button>
              </Gated>
            </div>
          ) : (
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer"
                    onClick={() => void navigate(`/accounting/receipts/${row.original.id}`)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* NFR-42 — the range, the total, and the two steps. */}
          {!isPending && !isError && (data?.total ?? 0) > 0 ? (
            <Pager
              page={paging.page}
              pages={range.pages}
              first={range.first}
              last={range.last}
              total={range.total}
              onPage={paging.setPage}
              label={t('receipts.title')}
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
